import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../context/ChatContext';
import { useAuthContext } from '../../context/AuthContext';
import { getProfileMe } from '../../services/profileService';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ProfessionalResponseForm } from './Cards/ProfessionalResponseForm';
import { ProposalCard } from './Cards/ProposalCard';
import { PaymentPendingCard } from './Cards/PaymentPendingCard';
import { ClosedSummaryCard } from './Cards/ClosedSummaryCard';
import PaymentIframeModal from '../ui/PaymentIframeModal/PaymentIframeModal';
import { paymentService } from '../../services/paymentService';
import '../../assets/styles/approved-chat.css';

export const ChatDrawer: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { 
        isChatDrawerOpen, 
        toggleChatDrawer, 
        conversations, 
        currentConversationId, 
        setCurrentConversationId, 
        currentMessages, 
        sendMessage,
        loadMessagesForConversation,
        onlineUsers,
        cancelBookingRequest,
        proposeBooking,
        rejectBooking,
        requestOtherTime,
        acceptBookingAndPay,
        temporaryRecipient
    } = useChat();

    const { user } = useAuthContext();
    const [inputValue, setInputValue] = useState('');
    const [myPicUrlState, setMyPicUrlState] = useState<string | null>(null);
    const [chatPaymentUrl, setChatPaymentUrl] = useState<string | null>(null);
    const conversationRef = useRef<HTMLDivElement>(null);

    const isChatClosed = () => {
        if (currentConversationId === -1) return false;
        const lastMsg = currentMessages[currentMessages.length - 1];
        if (!lastMsg) return false;
        if (lastMsg.messageType === 'system_event' && ['cancel_request', 'request_rejected', 'expired', 'confirmed'].includes(lastMsg.metadata?.event)) {
            return true;
        }
        return false;
    };
    
    const isClosed = isChatClosed();

    const activeConversation = currentConversationId === -1 && temporaryRecipient
        ? {
            id: -1,
            otherUser: {
                id: temporaryRecipient.id,
                uid: temporaryRecipient.uid || temporaryRecipient.id,
                firstName: temporaryRecipient.firstName,
                lastName: temporaryRecipient.lastName,
                profilePicture: temporaryRecipient.profilePicture
            },
            lastMessage: null,
            updatedAt: new Date().toISOString()
          }
        : (conversations.find(c => c.id === currentConversationId) || conversations[0]);

    useEffect(() => {
        if (isChatDrawerOpen && currentConversationId === null && activeConversation) {
            setCurrentConversationId(activeConversation.id);
        }
    }, [isChatDrawerOpen, activeConversation, currentConversationId, setCurrentConversationId]);

    useEffect(() => {
        if (!user) return;
        if (user.details?.image && typeof user.details.image === 'string') {
            const url = user.details.image.startsWith('http') ? user.details.image : `${process.env.REACT_APP_API_URL}${user.details.image}`;
            setMyPicUrlState(url);
        } else {
            getProfileMe().then(profile => {
                if (profile?.details?.image && typeof profile.details.image === 'string') {
                    const url = profile.details.image.startsWith('http') ? profile.details.image : `${process.env.REACT_APP_API_URL}${profile.details.image}`;
                    setMyPicUrlState(url);
                }
            }).catch(err => console.error("Error fetching fresh profile for chat", err));
        }
    }, [user]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isChatDrawerOpen && currentConversationId && currentConversationId !== -1) {
            loadMessagesForConversation(currentConversationId);
            
            // Bulletproof fallback: poll messages every 10s if socket is dead or unreliable
            interval = setInterval(() => {
                loadMessagesForConversation(currentConversationId);
            }, 10000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentConversationId, isChatDrawerOpen]);

    const messagesToRender = currentConversationId === -1 ? [] : currentMessages;

    useEffect(() => {
        if (conversationRef.current && isChatDrawerOpen) {
            setTimeout(() => {
                if (conversationRef.current) {
                    conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
                }
            }, 100);
        }
    }, [messagesToRender, isChatDrawerOpen]);

    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false);
    }, [activeConversation?.id]);

    if (!isChatDrawerOpen) return null;

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || !activeConversation) return;
        
        sendMessage(activeConversation.otherUser.id, inputValue, activeConversation.id);
        setInputValue('');
    };

    const profilePictureUrl = activeConversation?.otherUser?.profilePicture 
        ? (activeConversation.otherUser.profilePicture.startsWith('http') 
            ? activeConversation.otherUser.profilePicture 
            : `${process.env.REACT_APP_API_URL}${activeConversation.otherUser.profilePicture}`)
        : null;

    const initials = `${activeConversation?.otherUser?.firstName?.charAt(0) || ''}${activeConversation?.otherUser?.lastName?.charAt(0) || ''}`.toUpperCase() || 'M';

    return (
        <div className={classNames('drawer-layer', { 'is-open': isChatDrawerOpen })}>
            <div className="drawer-backdrop" onClick={toggleChatDrawer}></div>
            
            <article className="message-drawer chat-panel" aria-labelledby="chatTitle">
                <header className="drawer-head">
                    {profilePictureUrl && !imageError ? (
                        <img 
                            className="drawer-avatar" 
                            src={profilePictureUrl} 
                            alt={activeConversation?.otherUser?.firstName || 'Usuario'} 
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <span className="bw-menu-icon drawer-avatar notranslate" translate="no" style={{ background: '#e5ede7', color: '#4b705c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {initials}
                        </span>
                    )}
                    <div className="drawer-person">
                        <strong id="chatTitle">{activeConversation?.otherUser?.firstName} {activeConversation?.otherUser?.lastName}</strong>
                        <span>
                            <b className="viewer-label">
                                {onlineUsers.has(activeConversation?.otherUser?.id?.toString() || '') || onlineUsers.has(activeConversation?.otherUser?.uid?.toString() || '') 
                                    ? t('Chat.online', 'Conectado') 
                                    : t('Chat.offline', 'Desconectado')}
                            </b>
                        </span>
                    </div>
                    <div style={{marginLeft: 'auto', display: 'flex', gap: '8px'}}>
                        <button className="drawer-close" onClick={toggleChatDrawer} aria-label={t('Chat.close', 'Cerrar chat')}>×</button>
                    </div>
                </header>

                <div className="conversation" aria-live="polite" ref={conversationRef}>
                    {messagesToRender.map((msg, idx) => {
                        const isMine = msg.senderId === 'me' || msg.senderId === user?.uid?.toString() || msg.senderId === user?.id?.toString();
                        
                        const renderAvatar = (isSender: boolean) => {
                            if (isSender) {
                                const senderInitials = user?.first_name ? user.first_name.charAt(0).toUpperCase() : 'U';
                                
                                return myPicUrlState ? (
                                    <img className="message-avatar" src={myPicUrlState} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                    <div className="message-avatar notranslate" translate="no" style={{ background: '#e5ede7', color: '#4b705c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                        {senderInitials}
                                    </div>
                                );
                            } else {
                                return profilePictureUrl && !imageError ? (
                                    <img className="message-avatar" src={profilePictureUrl} alt="" onError={() => setImageError(true)} />
                                ) : (
                                    <div className="message-avatar notranslate" translate="no" style={{ background: '#e5ede7', color: '#4b705c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                        {initials}
                                    </div>
                                );
                            }
                        };

                        const isBookingActive = (bookingId: number, currentIndex: number) => {
                            for (let i = currentIndex + 1; i < messagesToRender.length; i++) {
                                const m = messagesToRender[i];
                                if (Number(m.metadata?.bookingId) === Number(bookingId)) {
                                    if (m.messageType === 'system_event' && ['cancel_request', 'request_rejected', 'expired', 'confirmed', 'request_other_time'].includes(m.metadata?.event)) return false;
                                    if (m.messageType === 'proposal') return false;
                                    if (m.messageType === 'payment_pending') return false;
                                }
                            }
                            return true;
                        };

                        if (msg.messageType === 'schedule_request') {
                            const { date, period, modality, note, bookingId, consultPrice, sessionType, currency } = msg.metadata || {};
                            const isActive = isBookingActive(bookingId, idx);
                            const displayModality = modality === 'Presencial' ? t('Chat.presential', 'Presencial') : modality === 'Virtual' ? t('Chat.virtual', 'Virtual') : modality;
                            const displayPeriod = period === 'Mañana' ? t('landing.professionalsDetail.bookingPanel.morning', 'Mañana') : period === 'Tarde' ? t('landing.professionalsDetail.bookingPanel.afternoon', 'Tarde') : period === 'Noche' ? t('landing.professionalsDetail.bookingPanel.evening', 'Noche') : period;
                            
                            const currencySymbol = currency === 'USD' ? 'US$' : '$U';
                            const displaySessionType = sessionType === 'initial' ? t('landing.professionalsDetail.bookingPanel.initialConsult', 'Inicial') : sessionType === 'followup' ? t('landing.professionalsDetail.bookingPanel.followupConsult', 'Seguimiento') : null;

                            return (
                                <div key={msg.id || idx} className="schedule-request-container">
                                    <article className="system-message">
                                        <div className="system-title">
                                            <span className="status-icon">✓</span>
                                            <span className="system-title-copy">
                                                <strong>{t('Chat.scheduleRequestSent', 'Solicitud de horario enviada')}</strong>
                                                <span>
                                                    {`Solicitud #SH-${bookingId} · `}
                                                    {new Date(msg.timestamp).toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'es-UY')}
                                                    {', '}
                                                    {new Date(msg.timestamp).toLocaleTimeString(i18n.language.startsWith('en') ? 'en-US' : 'es-UY', { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="summary-grid">
                                            {displaySessionType && (
                                                <div className="summary-item full">
                                                    <span>{t('landing.professionalsDetail.bookingPanel.consultTypeLabel', 'Tipo de consulta')}</span>
                                                    <strong>{displaySessionType}</strong>
                                                </div>
                                            )}
                                            <div className="summary-item">
                                                <span>{t('Chat.preferredDay', 'Día preferido')}</span>
                                                <strong>{date ? new Date(date).toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'es-UY', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }) : '—'}</strong>
                                            </div>
                                            <div className="summary-item">
                                                <span>{t('Chat.timeRange', 'Franja horaria')}</span>
                                                <strong>{displayPeriod || '—'}</strong>
                                            </div>
                                            <div className="summary-item">
                                                <span>{t('Chat.modality', 'Modalidad')}</span>
                                                <strong>{displayModality || '—'}</strong>
                                            </div>
                                            <div className="summary-item">
                                                <span>{t('Chat.referenceValue', 'Valor de referencia')}</span>
                                                <strong>{consultPrice ? `${currencySymbol} ${Number(consultPrice).toLocaleString('es-UY')}` : '—'}</strong>
                                            </div>
                                            {note && (
                                                <div className="summary-item full">
                                                    <span>{t('Chat.clarification', 'Aclaración')}</span>
                                                    <strong>{note}</strong>
                                                </div>
                                            )}
                                        </div>
                                        {isMine ? (
                                            <p className="system-foot">{t('Chat.requestDisclaimerPatient', 'La solicitud no constituye una reserva. Recibirás una notificación cuando la profesional responda o proponga un horario.')}</p>
                                        ) : (
                                            <p className="system-foot">{t('Chat.requestDisclaimerProfessional', 'La solicitud todavía no es una reserva.')}</p>
                                        )}
                                        {isActive && isMine && (
                                            <div className="chat-actions" style={{marginTop: '12px'}}>
                                                <button 
                                                    className="chat-button secondary" 
                                                    type="button"
                                                    onClick={() => {
                                                        if (bookingId && activeConversation) {
                                                            cancelBookingRequest(bookingId, activeConversation.id, activeConversation.otherUser.id);
                                                        }
                                                    }}
                                                >{t('Chat.cancelRequest', 'Cancelar solicitud')}</button>
                                            </div>
                                        )}
                                    </article>
                                    
                                    {isActive && !isMine && activeConversation && (
                                        <ProfessionalResponseForm
                                            bookingId={bookingId!}
                                            conversationId={activeConversation.id}
                                            recipientId={activeConversation.otherUser.id}
                                            initialDate={date}
                                            initialPeriod={period}
                                            initialModality={modality === 'Presencial' || modality === 'Virtual' ? modality : undefined}
                                            onPropose={async (bId, cId, rId, dates) => {
                                                await proposeBooking(bId, cId, rId, dates);
                                            }}
                                            onReject={async (bId, cId, rId, reason) => {
                                                await rejectBooking(bId, cId, rId, reason);
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        }

                        if (msg.messageType === 'proposal') {
                            const isActive = isBookingActive(msg.metadata?.bookingId, idx);
                            let inactiveReason = '';
                            if (!isActive) {
                                // Find what closed this proposal
                                const closingEvent = messagesToRender.slice(idx + 1).find(m => m.metadata?.bookingId === msg.metadata?.bookingId && (m.messageType === 'system_event' || m.messageType === 'proposal' || m.messageType === 'payment_pending'));
                                if (closingEvent?.metadata?.event === 'request_other_time') {
                                    const originalReq = messagesToRender.find(m => m.messageType === 'schedule_request' && Number(m.metadata?.bookingId) === Number(msg.metadata?.bookingId));
                                    
                                    let amIPatient = true;
                                    if (originalReq) {
                                        amIPatient = originalReq.senderId === 'me';
                                    } else {
                                        // If this proposal was sent by 'other', they are the professional, so I am the patient.
                                        amIPatient = msg.senderId !== 'me';
                                    }

                                    inactiveReason = t('Chat.inactiveReason.requestOther', '{{name}} solicitó otro horario.', { name: !amIPatient ? activeConversation?.otherUser.firstName : (user?.first_name || t('Chat.you', 'Tú')) });
                                } else if (closingEvent?.metadata?.event === 'request_rejected') {
                                    inactiveReason = t('Chat.inactiveReason.rejected', 'La propuesta fue rechazada.');
                                } else if (closingEvent?.messageType === 'proposal') {
                                    inactiveReason = t('Chat.inactiveReason.newProposal', 'Se envió una nueva propuesta.');
                                }
                            }

                            return (
                                <ProposalCard 
                                    key={idx}
                                    message={msg} 
                                    activeConversation={activeConversation!} 
                                    isActive={isActive}
                                    inactiveReason={inactiveReason || undefined}
                                    onAccept={async (selectedDate) => {
                                        const url = await acceptBookingAndPay(msg.metadata.bookingId, activeConversation!.id, activeConversation!.otherUser.id, selectedDate, msg.metadata.bookingUid);
                                        if (url) {
                                            setChatPaymentUrl(url);
                                        } else {
                                            alert(t('Chat.paymentStartError', 'No se pudo iniciar el pago.'));
                                        }
                                    }}
                                    onRequestOther={async () => {
                                        if (msg.metadata?.bookingId && activeConversation) {
                                            await requestOtherTime(msg.metadata.bookingId, activeConversation.id, activeConversation.otherUser.id);
                                        }
                                    }}
                                />
                            );
                        }

                        if (msg.messageType === 'system_event' && msg.metadata?.event === 'request_other_time') {
                            const isActive = isBookingActive(msg.metadata?.bookingId, idx);
                            
                            // Find the original schedule request for default values
                            const originalReq = messagesToRender.find(m => m.messageType === 'schedule_request' && Number(m.metadata?.bookingId) === Number(msg.metadata?.bookingId));
                            
                            // If we can't find originalReq, we can figure out our role by looking at who sent the most recent proposal
                            // If we sent the proposal, we are the professional.
                            const latestProposal = messagesToRender.find(m => m.messageType === 'proposal' && Number(m.metadata?.bookingId) === Number(msg.metadata?.bookingId));
                            
                            let amIPatient = true;
                            if (originalReq) {
                                amIPatient = originalReq.senderId === 'me';
                            } else if (latestProposal) {
                                amIPatient = latestProposal.senderId !== 'me';
                            } else {
                                amIPatient = user?.type_account !== 'professional';
                            }
                            
                            const professionalName = activeConversation?.otherUser?.firstName || t('Chat.theProfessional', 'el profesional');
                            const patientName = amIPatient ? (user?.first_name || t('Chat.you', 'Tú')) : activeConversation?.otherUser?.firstName;

                            const { date: initialDate, modality } = originalReq?.metadata || {};
                            const initialModality = modality === 'Presencial' || modality === 'Virtual' ? modality : undefined;

                            return (
                                <React.Fragment key={idx}>
                                    <article className="system-message">
                                        <div className="system-title">
                                            <span className="status-icon" style={{ backgroundColor: '#7da18e', color: 'white' }}>···</span>
                                            <span className="system-title-copy">
                                                <strong>{t('Chat.waitingForResponse', 'Esperando respuesta de {{name}}', { name: amIPatient ? professionalName : t('Chat.youLowercase', 'ti') })}</strong>
                                                <span>{t('Chat.notificationWhenProposal', '{{name}} recibirá una notificación cuando exista una propuesta.', { name: patientName })}</span>
                                            </span>
                                        </div>
                                    </article>
                                    
                                    {isActive && !amIPatient && activeConversation && (
                                        <ProfessionalResponseForm
                                            bookingId={msg.metadata.bookingId}
                                            conversationId={activeConversation.id}
                                            recipientId={activeConversation.otherUser.id}
                                            initialDate={initialDate}
                                            initialModality={initialModality}
                                            onPropose={async (bId, cId, rId, dates) => {
                                                await proposeBooking(bId, cId, rId, dates);
                                            }}
                                            onReject={async (bId, cId, rId, reason) => {
                                                await rejectBooking(bId, cId, rId, reason);
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        }

                        if (msg.messageType === 'system_event' && ['cancel_request', 'request_rejected', 'expired', 'confirmed'].includes(msg.metadata?.event)) {
                            return <ClosedSummaryCard key={idx} message={msg} activeConversation={activeConversation ?? undefined} />;
                        }

                        if (msg.messageType === 'payment_pending') {
                            return (
                                <PaymentPendingCard 
                                    key={idx}
                                    message={msg}
                                    onCompletePayment={async (bUid) => {
                                        try {
                                            const res = await paymentService.processPayment(bUid);
                                            if (res.payment_url) {
                                                setChatPaymentUrl(res.payment_url);
                                            } else {
                                                alert(t('Chat.paymentUrlError', 'Error al obtener URL de pago'));
                                            }
                                        } catch (e) {
                                            alert(t('Chat.paymentUrlError', 'Error al obtener URL de pago'));
                                        }
                                    }}
                                    onRequestOther={() => {
                                        sendMessage(activeConversation!.otherUser.id, t('Chat.requestOtherTimeText', 'Me gustaría solicitar otro horario diferente.'), activeConversation!.id);
                                    }}
                                />
                            );
                        }

                        return (
                            <div key={idx} className={classNames('message-row', { 'user': isMine, 'professional': !isMine })}>
                                {!isMine && renderAvatar(false)}
                                
                                <div className="chat-bubble">
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                                    <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.8, textAlign: isMine ? 'right' : 'left' }}>
                                        {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                </div>

                                {isMine && renderAvatar(true)}
                            </div>
                        );
                    })}
                </div>

                {currentMessages.some(m => m.messageType === 'confirmed') && (
                    <span className="thread-closed-note" style={{textAlign:'center', display:'block', fontSize:'11px', padding:'10px'}}>
                        {t('Chat.threadClosedBackupOnly', 'Hilo cerrado · disponible únicamente como respaldo')}
                    </span>
                )}

            <footer className="drawer-footer">
                <form className="drawer-composer" onSubmit={handleSend}>
                    <input 
                        type="text" 
                        aria-label={t('Chat.writeMessageAria', 'Escribir mensaje')} 
                        placeholder={isClosed ? t('Chat.threadClosedPlaceholder', 'Hilo cerrado') : t('Chat.writeMessagePlaceholder', 'Escribí un mensaje…')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        disabled={isClosed}
                    />
                    <button type="submit" disabled={isClosed}>{t('Chat.send', 'Enviar')}</button>
                </form>
            </footer>
            </article>

            {/* Payment Iframe Modal for chat-initiated payments */}
            <PaymentIframeModal
                open={!!chatPaymentUrl}
                onClose={() => setChatPaymentUrl(null)}
                url={chatPaymentUrl}
            />
        </div>
    );
};
